/**
 * Which Open Banking rail the provider uses to execute an automated payout.
 * Controlled by the OPEN_BANKING_PAYOUT_METHOD env var; never exposed to API callers.
 */
export enum OpenBankingPaymentInitiationMethod {
  PIS = 'pis',
  RTP = 'rtp'
}

export enum PaymentStatus {
  // Buyer has not yet acted (INIT at OF — link created, not clicked)
  RECEIVED = 'received',
  // Buyer submitted at their bank (RCVD at OF — buyer authorized, money in motion)
  AUTHORIZED = 'authorized',
  // Requires additional authorization beyond the buyer's own — e.g. a banker or special
  // approval (PENDING at OF)
  PENDING_ADDITIONAL_AUTHORIZATION = 'pending_additional_authorization',
  // Defensive: not in OF's documented lifecycle, kept as fallback
  PARTIALLY_ACCEPTED_TECHNICAL_CORRECT = 'partially_accepted_technical_correct',
  // Defensive: not in OF's documented lifecycle, kept as fallback
  ACCEPTED_FUNDS_CHECKED = 'accepted_funds_checked',
  // Terminal-success (Masav flows end at one of these three; FP flows end at COMPLETED)
  ACCEPTED_TECHNICAL_VALIDATION = 'accepted_technical_validation',
  ACCEPTED_WITH_CHANGE = 'accepted_with_change',
  ACCEPTED_SETTLEMENT_IN_PROCESS = 'accepted_settlement_in_process',
  // Terminal-success (FP / multi-auth / pending flows)
  ACCEPTED_SETTLEMENT_COMPLETED = 'accepted_settlement_completed',
  // Defensive: not in OF's documented lifecycle, kept as fallback
  ACCEPTED_SETTLEMENT_COMPLETED_CREDITOR_ACCOUNT = 'accepted_settlement_completed_creditor_account',
  // Terminal-failure
  REJECTED = 'rejected',
  CANCELLED = 'cancelled',
  /** Provider returned a technical error (ERROR webhook) — distinct from business rejection */
  ERROR = 'error'
}

/**
 * Payin statuses that represent genuine bank-side progress (multi-signer approval, or
 * additional banker/special authorization) — single source of truth for "which payin
 * statuses require extra approval," shared by `@sollapay/domain` (the pay-link retry rule)
 * and `@sollapay/database` (link-expiry bypass, lawyer resend/cancel gating).
 */
export const AWAITING_ADDITIONAL_AUTHORIZATION_STATUSES: readonly PaymentStatus[] = [
  PaymentStatus.PARTIALLY_ACCEPTED_TECHNICAL_CORRECT,
  PaymentStatus.PENDING_ADDITIONAL_AUTHORIZATION
];

export enum PaymentLifecycleStage {
  // Link delivered to buyer; buyer hasn't acted (OF INIT)
  SENT = 'sent',
  // Buyer authorized at their bank; money moving on the rails (OF RCVD+)
  IN_FLIGHT = 'in_flight',
  // Money committed at clearing
  COMPLETED = 'completed',
  // Rejected, errored, or cancelled
  FAILED = 'failed'
}

export enum TransferType {
  MASAV = 'masav',
  FP = 'fp',
  ZAHAV = 'zahav',
  BANK_CHECK = 'bank_check',
  VOUCHER = 'voucher'
}

/** The rails a buyer's payin can clear on. */
export const PAYIN_TRANSFER_TYPES = [TransferType.MASAV, TransferType.ZAHAV] as const;

/**
 * The rails a payout can clear on — `fp` is buyer-side only; a bank check and a voucher are paid
 * out only, and a voucher is reserved for the Tax Authority.
 */
export const PAYOUT_TRANSFER_TYPES = [
  TransferType.MASAV,
  TransferType.ZAHAV,
  TransferType.BANK_CHECK,
  TransferType.VOUCHER
] as const;

export type PayinTransferType = (typeof PAYIN_TRANSFER_TYPES)[number];

export type PayoutTransferType = (typeof PAYOUT_TRANSFER_TYPES)[number];

export enum PaymentProvider {
  OPEN_FINANCE = 'open_finance',
  FEEZBACK = 'feezback'
}

export enum BankTransactionStatus {
  BOOKED = 'BOOKED',
  PENDING = 'PENDING'
}

/** How a calendar day is treated by the Zahav (RTGS) system. */
export enum ZahavDayType {
  REGULAR = 'regular',
  SHORT = 'short',
  CLOSED = 'closed'
}
